import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { OneValueMeasurement } from "~/api/lastMeasurements";
import { Button } from "~/ui";
import { formatBackendDate, formatInteger } from "~/utils";
import { HealthDetails } from "..";
import { BloodPressureInputModal } from "./BloodPressureInputModal";

interface BloodPressureProps {
  systolicMeasurement: OneValueMeasurement | null;
  diastolicMeasurement: OneValueMeasurement | null;
  patientId: number;
}

export const BloodPressureCard = ({
  systolicMeasurement,
  diastolicMeasurement,
  patientId,
}: BloodPressureProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const val =
    systolicMeasurement && diastolicMeasurement
      ? `${formatInteger(systolicMeasurement.value)}/${formatInteger(diastolicMeasurement.value)}`
      : t("health.no_data");

  const handleViewMore = () => {
    void navigate(`/airmen/${patientId}/blood-pressure`);
  };

  const handleAddMeasurement = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setIsModalOpen(true);
  };

  // Collapsed card view
  return (
    <>
      <HealthDetails.Card
        onClick={handleViewMore}
        className="cursor-pointer transition-shadow duration-200 hover:shadow-lg"
      >
        <HealthDetails.CardHeader className="justify-between">
          <div className="flex items-center gap-2">
            <div className="text-primary-40 dark:text-primary-80">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 14L12 18L16 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <HealthDetails.CardTitle>
              {t("health.blood_pressure")}
            </HealthDetails.CardTitle>
          </div>

          <Button
            text="Log data"
            color="primary"
            onClick={handleAddMeasurement}
            className="px-3 py-0.5 [&_span]:text-sm"
          />
        </HealthDetails.CardHeader>
        <HealthDetails.CardBody>
          <HealthDetails.CardResult>{val}</HealthDetails.CardResult>
          <HealthDetails.CardDetails>
            <HealthDetails.CardUnit>{t("health.mmHg")}</HealthDetails.CardUnit>
            {systolicMeasurement && (
              <HealthDetails.CardDate>
                {formatBackendDate(systolicMeasurement.date)}
              </HealthDetails.CardDate>
            )}
          </HealthDetails.CardDetails>
          <div className="flex flex-1 items-center justify-end">
            <button className="text-sm text-primary-40 transition-colors hover:text-primary-30 dark:text-primary-80 dark:hover:text-primary-90">
              {t("general.view_more")}
            </button>
          </div>
        </HealthDetails.CardBody>
      </HealthDetails.Card>

      <BloodPressureInputModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        patientId={patientId}
      />
    </>
  );
};
